// Number / date formatting helpers.

export const CCY_SYMBOL = { USD: "$", INR: "₹", HKD: "HK$", CNY: "¥", EUR: "€", GBP: "£" };
export const MARKET_FLAG = { US: "🇺🇸", INDIA: "🇮🇳", CHINA: "🇨🇳" };
export const MARKET_LABEL = { US: "US", INDIA: "India", CHINA: "China" };

export function isNum(v) {
  return typeof v === "number" && Number.isFinite(v);
}

export function money(v, ccy, opts = {}) {
  if (!isNum(v)) return "—";
  const sym = CCY_SYMBOL[ccy] ?? (ccy ? `${ccy} ` : "");
  const abs = Math.abs(v);
  const digits = opts.digits ?? (ccy === "INR" || abs >= 1000 ? 0 : 2);
  const s = abs.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return `${v < 0 ? "−" : ""}${sym}${s}`;
}

export function pct(v, { sign = true, digits = 0 } = {}) {
  if (!isNum(v)) return "—";
  const s = Math.abs(v).toFixed(digits);
  if (v < 0) return `−${s}%`;
  return `${sign && v > 0 ? "+" : ""}${s}%`;
}

export function ratio(v, digits = 2) {
  if (!isNum(v)) return "—";
  return `${v.toFixed(digits)}×`;
}

export function num(v, digits = 1) {
  if (!isNum(v)) return "—";
  return v.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function compact(v) {
  if (!isNum(v)) return "—";
  const abs = Math.abs(v);
  const f = (n, s) => `${(v / n).toFixed(abs / n >= 100 ? 0 : 1)}${s}`;
  if (abs >= 1e12) return f(1e12, "T");
  if (abs >= 1e9) return f(1e9, "B");
  if (abs >= 1e6) return f(1e6, "M");
  if (abs >= 1e3) return f(1e3, "K");
  return String(Math.round(v));
}

export function int(v) {
  if (!isNum(v)) return "—";
  return Math.round(v).toLocaleString("en-US");
}

export function duration(seconds) {
  if (!isNum(seconds)) return "—";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
}

export function parseDate(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  // Run ids like 20260917T210718Z
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(String(v));
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]));
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(v, { time = true } = {}) {
  const d = parseDate(v);
  if (!d) return "—";
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (!time) return date;
  const t = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date}, ${t}`;
}

export function fmtDateDot(v) {
  const d = parseDate(v);
  if (!d) return "—";
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const t = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${t}`;
}

export function fmtTime(v) {
  const d = parseDate(v);
  if (!d) return String(v ?? "");
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function relTime(v) {
  const d = parseDate(v);
  if (!d) return "—";
  const diff = (d.getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const units = [
    [60, "s"],
    [3600, "m"],
    [86400, "h"],
    [Infinity, "d"],
  ];
  let n = abs;
  let label = "s";
  let prev = 1;
  for (const [limit, u] of units) {
    if (abs < limit) {
      n = Math.round(abs / prev);
      label = u;
      break;
    }
    prev = limit;
  }
  return diff < 0 ? `${n}${label} ago` : `in ${n}${label}`;
}

// Next calendar quarter start (1 Jan / 1 Apr / 1 Jul / 1 Oct) after the given date.
export function nextQuarterStart(from = new Date()) {
  const d = parseDate(from) || new Date();
  const q = Math.floor(d.getMonth() / 3);
  const nextMonth = (q + 1) * 3;
  const year = d.getFullYear() + (nextMonth >= 12 ? 1 : 0);
  const date = new Date(year, nextMonth % 12, 1);
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return { date, quarter };
}

export function toCSV(rows, columns) {
  const esc = (v) => {
    if (v == null) return "";
    const s = Array.isArray(v) ? v.join("|") : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c])).join(",")).join("\n");
  return `${head}\n${body}`;
}

export function downloadText(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
